// utils/codeCacheUtils.js
import { codeKeys } from '../codes/queries';
import { excerptKeys } from '../excerpts/queries';
import { normalizeId } from './idNormalization';

// Helper function to safely extract and normalize code ID from query key
function getCodeIdFromQueryKey(queryKey) {
  if (!Array.isArray(queryKey) || queryKey.length === 0) {
    return null;
  }
  const codeId = queryKey[queryKey.length - 1];
  return codeId == null ? null : normalizeId(codeId);
}

// Helper function to update all cached excerpt codings — the per-excerpt
// detail caches, the single store for excerpt objects under the ids-only
// model (every consumer, including the comparison overlay, resolves
// through them). `toCodeId: null` drops the codings instead (code delete).
function updateAllExcerptCodings(queryClient, fromCodeId, toCodeId) {
  const normalizedFromCodeId = normalizeId(fromCodeId);
  const normalizedToCodeId = toCodeId == null ? null : normalizeId(toCodeId);

  const excerptQueries = queryClient.getQueriesData({
    queryKey: excerptKeys.details(),
    exact: false
  });

  excerptQueries.forEach(([queryKey, excerptData]) => {
    if (!excerptData?.codings) return;

    const hasTargetCode = excerptData.codings.some(
      coding => normalizeId(coding.code_id) === normalizedFromCodeId
    );

    if (hasTargetCode) {
      const updatedCodings = normalizedToCodeId == null
        ? excerptData.codings.filter(coding => normalizeId(coding.code_id) !== normalizedFromCodeId)
        : excerptData.codings.map(coding =>
            normalizeId(coding.code_id) === normalizedFromCodeId
              ? { ...coding, code_id: normalizedToCodeId }
              : coding
          );

      queryClient.setQueryData(queryKey, {
        ...excerptData,
        codings: updatedCodings
      });
    }
  });
}

// Functional approach for code cache utilities
export function createCodeCacheUtils(queryClient) {
  return {
    async getSnapshot(codeId) {
      const normalizedCodeId = normalizeId(codeId);
      const previousCode = queryClient.getQueryData(codeKeys.detail(normalizedCodeId));
      let previousProjectCodes = null;

      if (previousCode?.project_id) {
        const normalizedProjectId = normalizeId(previousCode.project_id);
        const projectCodesKey = codeKeys.list(normalizedProjectId);
        previousProjectCodes = queryClient.getQueryData(projectCodesKey);
      }

      return { previousCode, previousProjectCodes };
    },

    async cancelQueries(codeId) {
      const normalizedCodeId = normalizeId(codeId);
      await queryClient.cancelQueries({ queryKey: codeKeys.detail(normalizedCodeId) });
      
      const code = queryClient.getQueryData(codeKeys.detail(normalizedCodeId));
      if (code?.project_id) {
        const normalizedProjectId = normalizeId(code.project_id);
        await queryClient.cancelQueries({ queryKey: codeKeys.list(normalizedProjectId) });
      }
    },

    updateCode(codeId, updates, options = {}) {
      const { skipProjectList = false } = options;
      const normalizedCodeId = normalizeId(codeId);

      // Update individual cache
      const currentCode = queryClient.getQueryData(codeKeys.detail(normalizedCodeId));
      if (currentCode) {
        queryClient.setQueryData(codeKeys.detail(normalizedCodeId), {
          ...currentCode,
          ...updates
        });
      }

      if (!skipProjectList && currentCode?.project_id) {
        // Update in project list
        const normalizedProjectId = normalizeId(currentCode.project_id);
        const projectCodesKey = codeKeys.list(normalizedProjectId);
        const projectCodes = queryClient.getQueryData(projectCodesKey);

        if (projectCodes?.codes) {
          const updatedCodes = projectCodes.codes.map(code => 
            normalizeId(code.id) === normalizedCodeId ? { ...code, ...updates } : code
          );

          queryClient.setQueryData(projectCodesKey, {
            ...projectCodes,
            codes: updatedCodes
          });
        }
      }
    },

    /**
     * Clean up stale code detail queries that no longer exist in the current codes list
     * @param {Array} currentCodes - The current list of codes from the API
     */
    cleanupStaleCodeQueries(currentCodes) {
      if (!currentCodes || !Array.isArray(currentCodes)) return;

      const currentCodeIds = new Set(
        currentCodes.map(code => normalizeId(code.id))
      );
      
      const existingQueries = queryClient.getQueriesData({
        queryKey: codeKeys.details()
      });
      
      existingQueries.forEach(([queryKey]) => {
        const normalizedCodeId = getCodeIdFromQueryKey(queryKey);
        
        // Remove if ID is null/undefined OR if it isn't in current codes
        if (!currentCodeIds.has(normalizedCodeId)) {
          queryClient.setQueryData(codeKeys.detail(normalizedCodeId), null);
        }
      });
    },
    /**
     * Replace an optimistic code ID with real server ID in all caches
     * @param {string} optimisticCodeId - The temporary UUID
     * @param {Object} realCode - The real code data from server
     * @param {string|number} projectId - The project ID
     */
    resolveOptimisticId(optimisticCodeId, realCode, projectId) {
      const normalizedProjectId = normalizeId(projectId);
      const normalizedRealCodeId = normalizeId(realCode.id);
      const optimisticCode = queryClient.getQueryData(codeKeys.detail(optimisticCodeId));

      // the code counts are updated optimistically
      const newCode = {
        ...optimisticCode,
        ...realCode,
        count: optimisticCode?.count || realCode.count || 0
      }
      
      // 1. Set the real code in individual cache
      queryClient.setQueryData(codeKeys.detail(normalizedRealCodeId), newCode);
      
      // 2. Remove optimistic code from individual cache
      queryClient.removeQueries({ queryKey: codeKeys.detail(optimisticCodeId) });
      
      // 3. Update project codes list - replace optimistic with real AND update parent_id references
      const projectCodesKey = codeKeys.list(normalizedProjectId);
      const projectCodes = queryClient.getQueryData(projectCodesKey);
      
      if (projectCodes?.codes) {
        const updatedCodes = projectCodes.codes.map(code => {
          // Replace the optimistic code with the real one
          if (code.id === optimisticCodeId) {
            return newCode;
          }
          // Update parent_id if it points to the optimistic ID
          if (code.parent_id === optimisticCodeId) {
            return { ...code, parent_id: normalizedRealCodeId };
          }
          return code;
        });

        queryClient.setQueryData(projectCodesKey, {
          ...projectCodes,
          codes: updatedCodes
        });
        
        // Update individual code caches for codes whose parent_id was updated
        updatedCodes.forEach(code => {
          if (code.parent_id === normalizedRealCodeId && 
              projectCodes.codes.find(c => c.id === code.id)?.parent_id === optimisticCodeId) {
            queryClient.setQueryData(codeKeys.detail(normalizeId(code.id)), code);
          }
        });
      }
      
      // 4. Update all excerpts that reference this optimistic code ID
      updateAllExcerptCodings(queryClient, optimisticCodeId, normalizedRealCodeId);
      
      return normalizedRealCodeId;
    },

    /**
     * Drop every cached coding for a deleted code — per-excerpt detail
     * caches and transcript bulk copies alike — so nothing keeps rendering
     * or classifying by a dead id until the refetches land.
     */
    removeCodeFromExcerpts(codeId) {
      updateAllExcerptCodings(queryClient, codeId, null);
    },

    /**
     * Rewrite a merged-away code's id to the surviving code's across every
     * cached excerpt (detail caches and transcript bulk copies) — the
     * remote-merge counterpart of resolveOptimisticId's rewrite.
     */
    mergeCodeInExcerpts(fromCodeId, toCodeId) {
      updateAllExcerptCodings(queryClient, fromCodeId, toCodeId);
    },

    restoreFromSnapshot(codeId, snapshot) {
      const { previousCode, previousProjectCodes } = snapshot;
      const normalizedCodeId = normalizeId(codeId);

      if (previousCode) {
        queryClient.setQueryData(codeKeys.detail(normalizedCodeId), previousCode);
      }

      if (previousCode?.project_id && previousProjectCodes) {
        const normalizedProjectId = normalizeId(previousCode.project_id);
        const projectCodesKey = codeKeys.list(normalizedProjectId);
        queryClient.setQueryData(projectCodesKey, previousProjectCodes);
      }
    },

    /**
     * Updates all cached excerpts to replace one code_id with another in their codings.
     * This is critical for code merge operations to maintain data integrity.
     *
     * @param {string|number} fromCodeId - The code ID to replace (the merged code)
     * @param {string|number} toCodeId - The code ID to replace with (the merge target)
     * @returns {number} The number of excerpts that were updated
     */
    updateExcerptCodingsForMerge(fromCodeId, toCodeId) {
      updateAllExcerptCodings(queryClient, fromCodeId, toCodeId);
    },

    /**
     * Removes code list caches that belong to a different project.
     * This ensures switching back to a previous project triggers a fresh fetch
     * (which repopulates code detail entries) rather than serving stale data.
     */
    removeOtherProjectCodeLists(projectId) {
      const normalizedProjectId = normalizeId(projectId);
      const codeListQueries = queryClient.getQueriesData({ queryKey: codeKeys.lists() });
      codeListQueries.forEach(([queryKey]) => {
        const cachedProjectId = queryKey[queryKey.length - 1];
        if (cachedProjectId && cachedProjectId !== normalizedProjectId) {
          console.log(`Removing code list cache for project ${cachedProjectId} due to switch to project ${normalizedProjectId}`);
          queryClient.removeQueries({ queryKey });
        }
      });
    },

    // NOTE: This currently is not being used anywhere, but may be useful in the future
    // It should maybe be used onError, but it was causing issues so will leave it out for now
    invalidateCaches(codeId, projectId = null) {
      const normalizedCodeId = normalizeId(codeId);
      queryClient.invalidateQueries({ queryKey: codeKeys.detail(normalizedCodeId) });

      if (!projectId) {
        const code = queryClient.getQueryData(codeKeys.detail(normalizedCodeId));
        projectId = code?.project_id;
      }

      if (projectId) {
        const normalizedProjectId = normalizeId(projectId);
        queryClient.invalidateQueries({ queryKey: codeKeys.list(normalizedProjectId) });
      }
    }
  };
}